import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HiExternalLink } from 'react-icons/hi';
import { BiCodeAlt } from 'react-icons/bi';

import {
  SectionTitle,
  Heading,
  BlueText,
  Button,
} from '../styles/Global.styled.js';

import {
  ProjectsSection,
  ProjectsWrapper,
  ProjectsGrid,
  ProjectCard,
  ProjectImageContainer,
  ProjectImage,
  ProjectInfo,
  TechStackContainer,
  TechStackCard,
  ProjectDescription,
  PlaceholderImage,
  FilterBar,
  FilterChip,
  FilterCount,
  EmptyState,
} from '../styles/MyProjects.styled.js';

import { projectDetails } from '../utils/Data.js';
import { fadeInTopVariant, fadeInBottomVariant } from '../utils/Variants.js';

const ALL_FILTER = 'All';

function MyProjects() {
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER);

  const filters = useMemo(() => {
    const counts = new Map();
    projectDetails.forEach((p) =>
      p.tech_stack.forEach((tech) =>
        counts.set(tech, (counts.get(tech) || 0) + 1)
      )
    );
    return [
      { name: ALL_FILTER, count: projectDetails.length },
      ...Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({ name, count })),
    ];
  }, []);

  const visibleProjects = useMemo(() => {
    if (activeFilter === ALL_FILTER) return projectDetails;
    return projectDetails.filter((p) => p.tech_stack.includes(activeFilter));
  }, [activeFilter]);

  return (
    <ProjectsSection id="Projects">
      <ProjectsWrapper>
        <motion.div
          variants={fadeInTopVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <SectionTitle align="center">Portfolio</SectionTitle>
          <Heading as="h2" size="h2" align="center">
            What <BlueText>I have built</BlueText>
          </Heading>
        </motion.div>

        <FilterBar role="tablist" aria-label="Project filters">
          {filters.map((filter) => {
            const isActive = filter.name === activeFilter;
            return (
              <FilterChip
                key={filter.name}
                type="button"
                role="tab"
                aria-selected={isActive}
                active={isActive}
                onClick={() => setActiveFilter(filter.name)}
              >
                {filter.name}
                <FilterCount>{filter.count}</FilterCount>
              </FilterChip>
            );
          })}
        </FilterBar>

        <ProjectsGrid as={motion.div} layout>
          <AnimatePresence mode="popLayout">
            {visibleProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                as={motion.div}
                layout
                variants={fadeInBottomVariant}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, y: 20, transition: { duration: 0.2 } }}
                transition={{ delay: Math.min(index * 0.05, 0.3) }}
              >
                <ProjectImageContainer>
                  {project.project_img ? (
                    <ProjectImage
                      src={project.project_img}
                      alt={project.project_name}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <PlaceholderImage>
                      <BiCodeAlt />
                    </PlaceholderImage>
                  )}
                </ProjectImageContainer>

                <ProjectInfo>
                  <TechStackContainer>
                    {project.tech_stack.map((stack) => (
                      <TechStackCard key={stack}>{stack}</TechStackCard>
                    ))}
                  </TechStackContainer>

                  <Heading as="h4" size="h4" bottom="0.5rem">
                    {project.project_name}
                  </Heading>

                  <ProjectDescription>{project.project_desc}</ProjectDescription>

                  <Button
                    as="a"
                    href={project.project_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Project <HiExternalLink size={16} />
                  </Button>
                </ProjectInfo>
              </ProjectCard>
            ))}
            {visibleProjects.length === 0 && (
              <EmptyState key="empty">
                No projects match this filter yet.
              </EmptyState>
            )}
          </AnimatePresence>
        </ProjectsGrid>
      </ProjectsWrapper>
    </ProjectsSection>
  );
}

export default MyProjects;
